import { useState } from "react";
import { Upload, Loader2, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FIELDS = [
  ["rating", "Voto (obbligatorio)"], ["date", "Data"], ["comment", "Commento"], ["title", "Titolo"], ["author", "Autore"],
  ["reply", "Risposta del negozio"], ["product_name", "Nome prodotto"], ["product_sku", "SKU / GTIN prodotto"],
  ["product_id", "ID prodotto WooCommerce"], ["order", "Numero ordine"],
];

const Mapping = ({ headers, mapping, setMapping }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    {FIELDS.map(([k, label]) => (
      <div key={k} className="space-y-1.5">
        <p className="text-sm font-medium">{label}</p>
        <Select value={mapping[k] == null ? "none" : String(mapping[k])} onValueChange={(v) => setMapping({ ...mapping, [k]: v === "none" ? null : Number(v) })}>
          <SelectTrigger data-testid={`import-map-${k}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Non importare —</SelectItem>
            {headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    ))}
  </div>
);

const Preview = ({ headers, rows }) => (
  <div className="overflow-x-auto border border-gray-200 rounded-md">
    <table className="text-xs w-full" data-testid="import-preview-table">
      <thead className="bg-gray-50"><tr>{headers.map((h, i) => <th key={i} className="text-left px-3 py-2 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-gray-100">{headers.map((_, j) => <td key={j} className="px-3 py-2 max-w-[260px] truncate">{r[j]}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

export default function ImportReviews() {
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [publish, setPublish] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/import/parse", fd);
      setParsed(data); setMapping(data.mapping);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import/commit", { session_id: parsed.session_id, mapping, publish });
      setResult(data); setParsed(null); toast.success(`${data.imported} recensioni importate`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fade-up max-w-5xl space-y-6">
      <PageHeader title="Importa da Trusted Shops" sub="Carica l'export CSV o Excel delle tue recensioni (eTrusted Control Centre → Recensioni → Esporta)" />
      <label className="block bg-white border-2 border-dashed border-gray-300 hover:border-emerald-600 transition-colors rounded-lg p-10 text-center cursor-pointer" data-testid="import-dropzone">
        <input type="file" accept=".csv,.xlsx,.txt" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="import-file-input" />
        {busy ? <Loader2 className="mx-auto animate-spin" /> : <Upload className="mx-auto text-emerald-700" />}
        <p className="mt-3 font-semibold">Seleziona il file da importare</p>
        <p className="text-sm text-gray-500">CSV (virgola, punto e virgola o tab) oppure XLSX · max 10 MB</p>
      </label>
      {result && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 flex gap-4 items-start" data-testid="import-result">
          <CheckCircle2 className="text-emerald-700 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-base">{result.imported} recensioni importate</p>
            <p className="text-gray-600">{result.shop} negozio · {result.product} prodotti · {result.duplicates} doppioni saltati · {result.invalid} righe senza voto valido</p>
          </div>
        </div>
      )}
      {parsed && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6" data-testid="import-mapping-panel">
          <div className="flex items-center gap-3"><FileSpreadsheet className="text-emerald-700" /><p className="font-semibold">{parsed.total} righe trovate · abbina le colonne</p></div>
          <Mapping headers={parsed.headers} mapping={mapping} setMapping={setMapping} />
          <div><p className="text-sm font-semibold mb-2">Anteprima (prime 5 righe)</p><Preview headers={parsed.headers} rows={parsed.preview} /></div>
          <p className="text-xs text-gray-500">Le righe con prodotto/SKU diventano recensioni prodotto, le altre recensioni del negozio. Data e voto originali vengono mantenuti; le recensioni mostrano "Importata da Trusted Shops".</p>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-3 text-sm"><Switch checked={publish} onCheckedChange={setPublish} data-testid="import-publish-switch" /> Pubblica subito (altrimenti finiscono "In attesa")</label>
            <Button className="bg-emerald-900 hover:bg-emerald-800" onClick={commit} disabled={busy || mapping.rating == null} data-testid="import-commit-button">
              {busy && <Loader2 className="animate-spin mr-2" size={16} />} Importa {parsed.total} righe
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
